import { sha256 } from '@noble/hashes/sha2.js';

/**
 * 随机 id 和文件校验和。**两样都不能依赖安全上下文**（CR-058）。
 *
 * `crypto.randomUUID()` 和 `crypto.subtle` 只在 https 和 localhost 下存在。一键部署的 http 测试模式
 * 把网页挂在 `http://服务器IP` 上，`npm run dev:lan` 挂在 `http://192.168.x.x` 上，两处都不是安全上下文 ——
 * 直接调用会抛「Cannot read properties of undefined」，网页端写日报、传图纸全部失败。
 * App 里不受影响（WebView 的来源是 http://localhost，算安全上下文）。
 */

/** 4 MB 一块：几百兆的图纸不用整个读进内存再算 */
const CHUNK_BYTES = 4 * 1024 * 1024;

/**
 * 32 位十六进制的随机 id。队列条目、附件的 clientId（幂等键）、草稿里的媒体都用它。
 * 服务端对 clientId 只校验是字符串，不要求 UUID 格式。
 */
export function randomId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return toHex(bytes);
}

/**
 * 原图的 SHA-256，用来证明存档文件未被篡改。
 *
 * 有 `crypto.subtle` 就用它（快）；没有就用 @noble/hashes 分块算，结果完全一样。
 */
export async function sha256Hex(blob: Blob): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const digest = await subtle.digest('SHA-256', await blob.arrayBuffer());
    return toHex(new Uint8Array(digest));
  }

  const hash = sha256.create();
  for (let offset = 0; offset < blob.size; offset += CHUNK_BYTES) {
    const chunk = await blob.slice(offset, offset + CHUNK_BYTES).arrayBuffer();
    hash.update(new Uint8Array(chunk));
  }
  return toHex(hash.digest());
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
